import type { LibSubscription } from 'lib/billing/pbAdapter';
import { BILLING_CYCLES, BILLING_SUBSCRIPTION_STATUSES, intervalToBillingCycle } from 'lib/billing';

// The value block every client billing event carries, so "who cancelled" can be
// sorted by how much they paid. Same names and cuts as org-insights'
// pkg/analytics/vocabulary.go — the backend emits the identical block on its own
// subscription:changed event, and `subscription_id` joins the two.
export type SubscriptionValueProps = {
  /** billing-manager subscription id — a subscription, never a person. */
  subscription_id: string;
  interval: string;
  period: string;
  seats: number;
  /** Per billing period, major units (45 = 45 usd). */
  amount: number;
  /** amount ÷ months in the period — the run-rate, comparable across periods. */
  monthly_amount: number;
  currency: string;
  size_tier: string;
  value_tier: string;
  products: string[];
  primary_product: string;
  line_count: number;
};

// PostHog period names. The FE's own cycle vocabulary (BILLING_CYCLES) predates
// this and differs in two spellings, so it is mapped rather than emitted raw.
export const PERIODS = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semiannual: 'semiannual',
  yearly: 'yearly'
} as const;

const PERIOD_BY_CYCLE: Record<string, string> = {
  [BILLING_CYCLES.monthly]: PERIODS.monthly,
  [BILLING_CYCLES.quarterly]: PERIODS.quarterly,
  [BILLING_CYCLES.semiAnnual]: PERIODS.semiannual,
  [BILLING_CYCLES.annual]: PERIODS.yearly
};

const MONTHS_BY_PERIOD: Record<string, number> = {
  [PERIODS.monthly]: 1,
  [PERIODS.quarterly]: 3,
  [PERIODS.semiannual]: 6,
  [PERIODS.yearly]: 12
};

// Tiers — keep in step with vocabulary.go; subscription-value.test.ts pins the cuts.
export const SIZE_TIERS = { zero: '0', single: '1', small: '2-5', medium: '6-10', large: '11+' } as const;
export const VALUE_TIERS = { zero: '0', low: '<25', mid: '25-99', high: '100-249', top: '250+' } as const;

// Upper bounds (inclusive for seats, exclusive for monthly amount) of each tier.
const SEATS_SMALL_MAX = 5;
const SEATS_MEDIUM_MAX = 10;
const MONTHLY_MID_FROM = 25;
const MONTHLY_HIGH_FROM = 100;
const MONTHLY_TOP_FROM = 250;

export function sizeTier(seats: number): string {
  if (seats <= 0) return SIZE_TIERS.zero;
  if (seats === 1) return SIZE_TIERS.single;
  if (seats <= SEATS_SMALL_MAX) return SIZE_TIERS.small;
  if (seats <= SEATS_MEDIUM_MAX) return SIZE_TIERS.medium;
  return SIZE_TIERS.large;
}

export function valueTier(monthly: number): string {
  if (monthly <= 0) return VALUE_TIERS.zero;
  if (monthly < MONTHLY_MID_FROM) return VALUE_TIERS.low;
  if (monthly < MONTHLY_HIGH_FROM) return VALUE_TIERS.mid;
  if (monthly < MONTHLY_TOP_FROM) return VALUE_TIERS.high;
  return VALUE_TIERS.top;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

const EMPTY: SubscriptionValueProps = {
  subscription_id: '',
  interval: '',
  period: '',
  seats: 0,
  amount: 0,
  monthly_amount: 0,
  currency: '',
  size_tier: SIZE_TIERS.zero,
  value_tier: VALUE_TIERS.zero,
  products: [],
  primary_product: '',
  line_count: 0
};

/** A subscription as the UI holds it — possibly partial, never trusted. */
export type SubscriptionInput = Partial<LibSubscription>;

/**
 * Derives the value block from a subscription the UI already holds. Tolerant of a
 * missing or partial subscription — analytics must never block a cancel.
 */
export function subscriptionValue(sub?: SubscriptionInput | null): SubscriptionValueProps {
  if (!sub) return EMPTY;
  const lines = Array.isArray(sub.lineItems) ? sub.lineItems : [];

  let seats = 0;
  let amount = 0;
  let primary = '';
  let best = -1;
  const products: string[] = [];
  for (const line of lines) {
    const qty = Number(line.quantity) || 0;
    const lineAmount = qty * (Number(line.unitAmount) || 0);
    seats += qty;
    amount += lineAmount;
    if (line.productName) {
      products.push(line.productName);
      if (lineAmount > best) {
        primary = line.productName;
        best = lineAmount;
      }
    }
  }

  const interval = sub.interval ?? '';
  const period = PERIOD_BY_CYCLE[intervalToBillingCycle(interval, sub.intervalCount ?? 1)] ?? PERIODS.monthly;
  const monthly = amount / (MONTHS_BY_PERIOD[period] ?? 1);

  return {
    subscription_id: sub.id ?? '',
    interval,
    period,
    seats,
    amount: round2(amount),
    monthly_amount: round2(monthly),
    currency: sub.currency ?? '',
    size_tier: sizeTier(seats),
    value_tier: valueTier(monthly),
    products,
    primary_product: primary,
    line_count: lines.length
  };
}

// What a customer is worth right now, across every live subscription — set as
// PERSON properties so the Persons list can be sorted by value at any time, not
// only when someone fires a cancel event. Prefixed so they never collide with
// the per-event block above.
export type SubscriptionSummaryProps = {
  subscription_count: number;
  subscription_seats: number;
  subscription_monthly_amount: number;
  subscription_value_tier: string;
  subscription_products: string[];
};

/** Sums the live (non-cancelled) subscriptions into the person-level value. */
export function subscriptionSummary(subs?: SubscriptionInput[] | null): SubscriptionSummaryProps {
  const live = (subs || []).filter((s) => s.status !== BILLING_SUBSCRIPTION_STATUSES.canceled);
  const products = new Set<string>();
  let seats = 0;
  let monthly = 0;
  for (const sub of live) {
    const v = subscriptionValue(sub);
    seats += v.seats;
    monthly += v.monthly_amount;
    v.products.forEach((p) => products.add(p));
  }
  return {
    subscription_count: live.length,
    subscription_seats: seats,
    subscription_monthly_amount: round2(monthly),
    subscription_value_tier: valueTier(monthly),
    subscription_products: Array.from(products).sort()
  };
}
